import { useEffect, useState } from "react";
import { Alert, Anchor, Card, Center, Code, Loader, Stack, Text, Title } from "@mantine/core";
import { Outlet, useLocation } from "react-router";
import { adminGet, beginOAuthLogin, AdminApiError } from "../api.ts";

const checkOAuthEnv = (): string | null => {
  const e = import.meta.env as Record<string, string | undefined>;
  const missing: string[] = [];
  if (!e["VITE_MIP_OAUTH_ISSUER"]) missing.push("VITE_MIP_OAUTH_ISSUER");
  if (!e["VITE_MIP_OAUTH_CLIENT_ID"]) missing.push("VITE_MIP_OAUTH_CLIENT_ID");
  if (!e["VITE_MIP_OAUTH_REDIRECT_URI"]) missing.push("VITE_MIP_OAUTH_REDIRECT_URI");
  return missing.length === 0 ? null : missing.join(" / ");
};

export interface AdminMe {
  readonly tgUserId: number;
  readonly telegramUsername: string | null;
  readonly name: string;
  readonly avatarUrl: string | null;
  readonly groups: ReadonlyArray<string>;
  readonly role: "owner" | "operator";
}

let cachedMe: AdminMe | null = null;
const listeners = new Set<(me: AdminMe | null) => void>();

export const getCachedMe = (): AdminMe | null => cachedMe;
export const setCachedMe = (me: AdminMe | null): void => {
  cachedMe = me;
  for (const l of listeners) l(me);
};
export const subscribeMe = (fn: (me: AdminMe | null) => void): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export function OAuthGuard() {
  const [ready, setReady] = useState(cachedMe !== null);
  const [envMissing, setEnvMissing] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    if (cachedMe !== null) {
      setReady(true);
      return;
    }
    void (async () => {
      console.warn("[mip-oauth-debug] OAuthGuard 无 cachedMe，请求 /auth/me", {
        path: location.pathname + location.search,
        at: new Date().toISOString(),
      });
      try {
        const me = await adminGet<AdminMe>("/auth/me");
        setCachedMe(me);
        setReady(true);
        console.warn("[mip-oauth-debug] OAuthGuard /auth/me 成功", {
          tgUserId: me.tgUserId,
          role: me.role,
        });
      } catch (e) {
        const err = e as AdminApiError;
        if (err.status === 401 || err.status === 403) {
          console.warn("[mip-oauth-debug] OAuthGuard /auth/me 鉴权失败 -> beginOAuthLogin", {
            status: err.status,
            code: err.code,
            message: err.message,
            path: location.pathname + location.search,
            at: new Date().toISOString(),
          });
          const missing = checkOAuthEnv();
          if (missing !== null) {
            setEnvMissing(missing);
            setReady(true);
            return;
          }
          try {
            await beginOAuthLogin(location.pathname + location.search, "OAuthGuard.auth/me");
          } catch (oauthErr) {
            setOauthError((oauthErr as Error).message ?? String(oauthErr));
            setReady(true);
          }
        } else {
          console.error("[mip-oauth-debug] OAuthGuard /auth/me 非鉴权失败", err);
          setOauthError(`${err.code ?? "INTERNAL_ERROR"}: ${err.message ?? "未知错误"}`);
          setReady(true);
        }
      }
    })();
  }, [location.pathname, location.search]);

  if (envMissing !== null) {
    return (
      <Center style={{ minHeight: "100vh", padding: 24 }}>
        <Card withBorder padding="lg" radius="md" style={{ maxWidth: 640 }}>
          <Stack gap="md">
            <Title order={3}>OAuth Client 未配置</Title>
            <Alert color="red" title="缺少 Vite 公开环境变量">
              <Text size="sm">以下 env 为空，无法跳转 OAuth Gateway 登录页：</Text>
              <Code block mt="xs">
                {envMissing}
              </Code>
            </Alert>
            <Text size="sm">
              请到 Telegram 私聊 OAuth Gateway 管理 bot 创建 <Code>mip-turntable-admin</Code>{" "}
              应用，把 <Code>client_id</Code> 与 <Code>redirect_uri</Code> 填入根目录{" "}
              <Code>.env</Code>：
            </Text>
            <Code block>
              {`VITE_MIP_OAUTH_CLIENT_ID=<由 Gateway 生成>
VITE_MIP_OAUTH_REDIRECT_URI=<ngrok 或 prod 域名>/oauth/callback
MIP_OAUTH_CLIENT_ID=<同上 client_id>
MIP_OAUTH_CLIENT_SECRET=<由 Gateway 生成，仅后端>
MIP_OAUTH_REDIRECT_URI=<同 VITE_>`}
            </Code>
            <Text size="sm" c="dimmed">
              改完 <Code>.env</Code> 后重启 frontend-admin dev server（Vite 仅在启动时读取 VITE_
              变量）。
            </Text>
            <Text size="sm">
              详细步骤见{" "}
              <Anchor href="/docs/dev-quickstart.md" target="_blank">
                docs/dev-quickstart.md
              </Anchor>
              §「OAuth Client 首次准备」。
            </Text>
          </Stack>
        </Card>
      </Center>
    );
  }

  if (oauthError !== null) {
    return (
      <Center style={{ minHeight: "100vh", padding: 24 }}>
        <Card withBorder padding="lg" radius="md" style={{ maxWidth: 560 }}>
          <Stack gap="sm">
            <Title order={3}>登录失败</Title>
            <Alert color="red">
              <Code block>{oauthError}</Code>
            </Alert>
          </Stack>
        </Card>
      </Center>
    );
  }

  if (!ready) {
    return (
      <Center style={{ minHeight: "100vh" }}>
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text c="dimmed">正在校验登录...</Text>
        </Stack>
      </Center>
    );
  }
  return <Outlet />;
}
